import React, { useState, useEffect, useMemo } from "react";
import Section from "../sections/Section";
import arePropsEqual from "../../utils/arePropsEqual";
import { buildTabsStylesheet } from "./ElementTabsStyleResolver";
import BuilderStyleTag from "../shared/BuilderStyleTag";

const ElementTabs = (props) => {
  let element = props.element;
  const classes = style.classes(element, props.builder, props.device);
  const stylesheet = buildTabsStylesheet(
    props.builder,
    props.device,
    props.cta,
    element,
    props.variant
  );

  let sorted_tabs_items = Object.values(element.content_toggle_items)
    .filter(
      (tab_item) =>
        tab_item.element_id == element.id && tab_item.toBeDeleted !== true
    )
    .sort((a, b) => a.position - b.position);

  const [visibleTabPosition, setVisibleTabPosition] = useState(
    element.options["tabs-open-item-position"] || 1
  );

  const selectTabPosition = (position) => {
    setVisibleTabPosition(position);

    let updates = [
      {
        object_type: "elements",
        object_id: element.id,
        setting_name: "[options][tabs-open-item-position]",
        value: position,
        skip_history: false,
      },
    ];

    dispatchCustomEvent("updateBuilder", { updates: updates });
  }

  const handleTabsListContextMenu = (e) => {
    e.preventDefault();
    document.dispatchEvent(
      new CustomEvent("showPopover", {
        detail: {
          object_type: "elements",
          object_id: element.id,
          tab: "Content",
          object: element,
          anchorPosition: { x: e.clientX, y: e.clientY },
        },
      })
    );
  };

  useEffect(() => {
    document.addEventListener(`cf_element_${element.id}_select_toggle_item`, (e) => {
      selectTabPosition(e.detail.toggle_item.position);
    });

    return () => {
      document.removeEventListener(`cf_element_${element.id}_select_toggle_item`, (e) => {
        selectTabPosition(e.detail.toggle_item.position);
      });
    };
  }, []);

  const sortedTabs = useMemo(() => {
    return (
      <React.Fragment>
        {sorted_tabs_items.map((tab_item) => {
          let tab_item_active = visibleTabPosition
            ? visibleTabPosition == tab_item.position
            : tab_item.position == 1;

          return (
            <div
              onClick={() => { selectTabPosition(tab_item.position) }}
              key={`cf-tab-item-${tab_item.id}`}
              className={`cf-merge-tag-allowed cf-tab-item ${tab_item_active ? "active" : ""}`}
              data-section-id={
                tab_item.section ? tab_item.section.id : undefined
              }
              data-tab-id={tab_item.id}
              data-tab-pos={tab_item.position}
              data-tab-element-id={tab_item.element_id}
              dangerouslySetInnerHTML={{
                __html: tab_item.content,
              }}
              role="tab"
              tabIndex="0"
              aria-controls={`cf-tab-item-${tab_item.id}`}
              aria-selected={tab_item_active}
            ></div>
          );
        })}
      </React.Fragment>
    );
  }, [sorted_tabs_items, visibleTabPosition]);

  const sortedSections = useMemo(() => {
    return (
      <React.Fragment>
        {sorted_tabs_items.map((tab_item) => {
          const tab_item_section = Object.values(element.sections).filter(
            (section) =>
              section.content_toggle_item_id == tab_item.id &&
              section.step_id == props.section.step_id
          )[0];

          const tab_visible = tab_item.position == visibleTabPosition;

          if (props.builder == true && !tab_visible) {
            return null;
          }

          if (tab_item_section) {
            return (
              <div
                id={`cf-tab-item-${tab_item.id}-section-${tab_item_section.id}`}
                key={`cf-tab-item-${tab_item.id}-section-${tab_item_section.id}`}
                className="cf-tab-item-cf-section-box"
                data-tab-element-id={element.id}
                data-tab-id={tab_item.id}
                style={{ display: tab_visible ? "block" : "none" }}
                role="tabpanel"
                aria-labelledby={`cf-tab-item-${tab_item.id}`}
                aria-hidden={!tab_visible}
              >
                <Section
                  website={props.website}
                  cta={props.cta}
                  variant={props.variant}
                  builder={props.builder}
                  preview={props.preview}
                  step={props.step}
                  section={tab_item_section}
                  device={props.device}
                ></Section>
              </div>
            );
          }
        })}
      </React.Fragment>
    );
  }, [sorted_tabs_items, visibleTabPosition, element.sections, props.section.step_id, props.device]);

  return (
    <React.Fragment>
      <div
        className={classes}
        data-element-id={element.id}
        data-font={
          element.options["tabs-font-family"] ||
          props.variant.options["tabs-font-family"]
        }
      >
        <div className={`cf-tabs-container`}>
          <div
            className="cf-tabs-list"
            role="tablist"
            onContextMenu={handleTabsListContextMenu}
            data-tab-element-id={element.id}
          >
            {sortedTabs}
          </div>
          <div className="cf-section-box" data-tab-element-id={element.id}>{sortedSections}</div>
        </div>

        <BuilderStyleTag
          builder={props.builder}
          styleKey={`cta:${props.cta.id}:element:${element.id}:tabs`}
          stylesheet={stylesheet}
        />
      </div>
    </React.Fragment>
  );
};

const style = {
  classes: (element, builder, device) => {
    let options = element.options;
    let classes = "element-content cf-tabs-element ";
    let alignmentClass = "cf-outer-center";

    if (device == "mobile") {
      if (options["cf-alignment-mobile"]) {
        if (options["cf-alignment-mobile"].indexOf("left") > -1) {
          alignmentClass = "cf-outer-left";
        } else if (options["cf-alignment-mobile"].indexOf("center") > -1) {
          alignmentClass = "cf-outer-center";
        } else if (options["cf-alignment-mobile"].indexOf("right") > -1) {
          alignmentClass = "cf-outer-right";
        }
      }
    } else {
      if (options["cf-alignment"]) {
        if (options["cf-alignment"].indexOf("left") > -1) {
          alignmentClass = "cf-outer-left";
        } else if (options["cf-alignment"].indexOf("center") > -1) {
          alignmentClass = "cf-outer-center";
        } else if (options["cf-alignment"].indexOf("right") > -1) {
          alignmentClass = "cf-outer-right";
        }
      }
    }

    classes += " " + alignmentClass;

    return classes;
  },
};

export default React.memo(ElementTabs, arePropsEqual);
